import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useMetaTags from '../hooks/useMetaTags';
import MatrixRain from '../components/MatrixRain';
import MobileMenu from '../components/MobileMenu';
import { getPosts } from '../lib/sanity';

const Home = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [recentPosts, setRecentPosts] = useState([]);

  useMetaTags({
    title: 'Sam Penny - Experienced Growth & SEO Consultant',
    description: 'Melbourne-based SEO consultant with 10+ years\' experience. I help brands grow through strategy, content, and technical SEO that drives results.'
  });

  useEffect(() => {
    const loadRecentPosts = async () => {
      try {
        const posts = await getPosts();
        setRecentPosts(posts.slice(0, 2)); // Get the two most recent posts
      } catch (error) {
        console.error('Error loading recent posts:', error);
      }
    };
    loadRecentPosts();
  }, []);

  return (
    <div className="App homepage">
      <div className="hero-section">
        <div className="hero-content-wrapper">
          <div className="hero-content">
            <h1>Sam Penny</h1>
            <div className="hero-subhead">Growth & SEO Consultant</div>
            <p>Based in Melbourne. I work with brands to take the guesswork out of growth, using SEO and other channels to build lasting visibility.</p>
            <div className="hero-buttons">
              <Link to="/blog" className="cta-button">Read Blog</Link>
              <Link to="/contact" className="cta-button outline">Contact Me</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="about-section" id="about-content">
        <div className="about-content">
          <div className="about-image" style={{ backgroundImage: `url('/images/sam-cartoon.png')` }} role="img" aria-label="Sam Penny portrait"></div>
          <div className="about-text">
            <h2>Background & Experience</h2>
            <p>I lead SEO strategy at Rest Super, a $93B+ superannuation fund, bringing nearly a decade of experience across both agency and in-house roles.</p>
            <p>I've had the privilege of speaking on a number of podcasts and at conferences, such as Melbourne's SEO Collective and Sydney's Search Marketing Summit.</p>
            <p>First and foremost, I am a marketer. There's no one-size-fits-all approach to organic growth; I bring real experience across e-comm, finance, SaaS, and local SEO, for clients big and small. When we work together, I do the work to understand your business and formulate a strategy that works for you.</p>
          </div>
        </div>
      </div>

      <section className="services-section">
        <div className="services-container">
          <h2>What I can help with</h2>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-accent"></div>
              <h3>Growth Strategy</h3>
              <p>Grow your own way. I'll help you design a growth strategy designed around your strengths.</p>
            </div>
            <div className="service-card">
              <div className="service-accent"></div>
              <h3>SEO Audits</h3>
              <p>Want to make sure there's nothing holding your site back, or have you experienced a traffic drop? An in-depth audit could be the solution.</p>
            </div>
            <div className="service-card">
              <div className="service-accent"></div>
              <h3>Technical SEO</h3>
              <p>This becomes more important as your site grows: you want to make sure you have strong technical foundations as you scale.</p>
            </div>
            <div className="service-card">
              <div className="service-accent"></div>
              <h3>SEO & Content Strategy</h3>
              <p>I'll help you map out the best path to growth across search. I'll build your strategy and help train your writers so you can get the best out of them.</p>
            </div>
            <div className="service-card">
              <div className="service-accent"></div>
              <h3>Paid Ads</h3>
              <p>Sometimes SEO isn't the solution: Paid Ads across Google, Meta, and TikTok offer instant ROI when results are crucial.</p>
            </div>
            <Link to="/contact" className="service-card contact-card">
              <div className="service-accent"></div>
              <h3>Get in touch →</h3>
              <p>Let's discuss how I can help you achieve your growth goals through effective SEO and digital strategy.</p>
            </Link>
          </div>
        </div>
      </section>

      <section className="why-hire-me-section">
        <div className="why-hire-me-content">
          <div className="why-hire-me-text">
            <h2>Why hire me</h2>
            <p>Hiring an agency can be frustrating. You're often paying top dollar for a junior specialist with only a few years of experience, not to mention their Google-style offices, team Christmas party with open bar, and those single-origin coffee beans flown in from Peru.</p>
            <p>Work with me and you get true value: a dedicated specialist with the level of experience you rarely find at an agency.</p>
          </div>
          <div className="why-hire-me-image">
            <img src="/images/bean.png" alt="Coffee bean illustration" className="bean-image" />
          </div>
        </div>
      </section>

      <section className="worked-with-section" aria-label="Clients I've worked with">
        <h2>I've had the pleasure of working with</h2>
        <div className="worked-with-logos">
          <div className="logo-container">
            <img src="/images/logos/youi.png" alt="Youi Insurance" loading="lazy" />
          </div>
          <div className="logo-container">
            <img src="/images/logos/raa.png" alt="RAA Insurance" loading="lazy" />
          </div>
          <div className="logo-container">
            <img src="/images/logos/lyka.png" alt="Lyka Pet Food" loading="lazy" />
          </div>
          <div className="logo-container">
            <img src="/images/logos/rest.png" alt="Rest Super" loading="lazy" />
          </div>
          <div className="logo-container">
            <img src="/images/logos/lsv.png" alt="Life Saving Victoria" loading="lazy" />
          </div>
          <div className="logo-container">
            <img src="/images/logos/balibody.png" alt="Bali Body" loading="lazy" />
          </div>
        </div>
      </section>

      <div className="recent-articles-section">
        <h2>From my brain to yours 🧠</h2>
        <div className="articles-grid">
          {recentPosts.map(post => (
            <Link key={post._id} to={`/blog/${post.slug}`} className="article-card">
              <div className="article-image" style={{ 
                backgroundImage: post.mainImage?.asset?.url 
                  ? `url('${post.mainImage.asset.url}')` 
                  : `url('/images/default-blog.png')`
              }}></div>
              <div className="article-content">
                <h3>{post.title}</h3>
                <div className="article-link">VIEW ARTICLE →</div>
              </div>
            </Link>
          ))}
          {recentPosts.length === 0 && (
            <div className="no-posts-message">
              <p>New articles coming soon!</p>
              <Link to="/blog" className="cta-button">View All Posts</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home; 